import type { AxiosPromise } from 'axios';
import type { SubscriptionStatus } from '~/constants/contactSubscription';
import type { BaseClientConfig, ExtendedAxiosRequestConfig } from '~/services/BaseClient';
import axiosCacheInterceptor from '~/services/axiosCacheInterceptor';

export const axiosCacheInterceptorRequestConfig: ExtendedAxiosRequestConfig = {
    cache: {
        interpretHeader: false,
        cacheTakeover: false,
        methods: ['post', 'get'],
    },
};

export const analyticsClientConfig: BaseClientConfig = {
    client: axiosCacheInterceptor,
    apiBase: window.Spark?.apiBase,
};

export type UsageType = 'mms_message' | 'sms_segment';
export type ContactType = 'affiliated' | 'preferred';
export type AnalyticsDateRange = [string, string, string?];
export type AnalyticsDateRangeParam = { range: AnalyticsDateRange };

export interface MarketingSubscriptionsByStatusResponse {
    object: string;
    data: {
        object: string;
        subscription_status: SubscriptionStatus;
        team_id: number;
        value: number;
    }[];
}

export interface StatisticsResponse {
    object: 'statistics';
    team_id: number;
    values: number[];
}

export interface EntryPointStatisticsResponse {
    object: string;
    data: {
        object: string;
        entry_point: string;
        team_id: number;
        values: number[];
    }[];
}

export interface ActiveEntryPointsResponse {
    object: string;
    team_id: number;
    value: number;
}

export interface UsageTotalByTypeResponse {
    object: 'list';
    data: {
        object: 'usage_type_statistics';
        team_id: number;
        usage_type: UsageType;
        values: number[];
    }[];
}

export interface WorkflowVersionPair {
    workflow_id: number;
    version: number;
}

export type WorkflowVersionEnteredTotalsParams = AnalyticsDateRangeParam & {
    versions: WorkflowVersionPair[];
};

export interface WorkflowVersionEnteredTotalsResponse {
    object: 'list';
    data: {
        object: 'workflow_version_entered_total';
        team_id: number;
        workflow_id: number;
        version: number;
        entered: number;
    }[];
}

export interface AnalyticsClientInterface {
    getMarketingSubscriptionsTotalByStatus(type?: ContactType): AxiosPromise<MarketingSubscriptionsByStatusResponse>;
    getMarketingSubscriptionsTotalOptIns(
        params: AnalyticsDateRangeParam,
        type?: ContactType
    ): AxiosPromise<StatisticsResponse>;
    getMarketingSubscriptionsTotalOptOuts(
        params: AnalyticsDateRangeParam,
        type?: ContactType
    ): AxiosPromise<StatisticsResponse>;
    getContactsTotalCreated(params: AnalyticsDateRangeParam, type?: ContactType): AxiosPromise<StatisticsResponse>;

    getMarketingSubscriptionsTotalOptOutsByEntryPoint?(
        params: AnalyticsDateRangeParam
    ): AxiosPromise<EntryPointStatisticsResponse>;
    getMarketingSubscriptionsTotalOptInsByEntryPoint?(
        params: AnalyticsDateRangeParam
    ): AxiosPromise<EntryPointStatisticsResponse>;
    getContactsTotalCreatedByEntryPoint?(params: AnalyticsDateRangeParam): AxiosPromise<EntryPointStatisticsResponse>;
    getContactsTotalActiveEntryPoints?(params?: AnalyticsDateRangeParam): AxiosPromise<ActiveEntryPointsResponse>;
    getUsageTotalByType?(
        params: AnalyticsDateRangeParam & { filters: { usage_types: UsageType[] } }
    ): AxiosPromise<UsageTotalByTypeResponse>;
    getWorkflowVersionEnteredTotals?(
        params: WorkflowVersionEnteredTotalsParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<WorkflowVersionEnteredTotalsResponse>;
}
